import React, { useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, IconButton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { wealthService, apiErrorMessage } from '../../utils/api';
import { formatAsOf, formatINR, formatUnits, titleCase } from './format';

const DIVIDER = '1px dashed #d0d0d0';
const ACCOUNT_LABEL = { MUTUAL_FUND: 'Folio', EQUITY: 'Demat', NPS: 'PRAN', BANK: 'A/c' };

function HoldingsList({ category, onBack }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    wealthService.getHoldings(category)
      .then(setData)
      .catch((err) => setError(apiErrorMessage(err, 'Could not load holdings.')));
  }, [category]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={onBack} aria-label="Back to your wealth" edge="start">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ color: 'primary.main' }}>{data?.label || 'Holdings'}</Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {!data && !error && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

      {data && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>{formatINR(data.total_value)}</Typography>
            {data.as_of && (
              <Typography variant="caption" color="text.secondary">Fetched on {formatAsOf(data.as_of)}</Typography>
            )}
          </Box>

          {/* No asset-type chips for mutual funds (MF Central's labels don't match our classification) */}
          {category !== 'MUTUAL_FUND' && data.breakdown?.length > 1 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {data.breakdown.map((b) => (
                <Chip key={b.key} size="small" variant="outlined" label={`${titleCase(b.key)} · ${formatINR(b.value)}`} />
              ))}
            </Box>
          )}

          {data.holdings.length === 0 && (
            <Typography color="text.secondary">No holdings were found.</Typography>
          )}

          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }} aria-label={`${data.label} holdings`}>
            {data.holdings.map((h, i) => {
              const gain = h.gain_loss;
              return (
                <Box component="li" key={`${h.account_ref}-${h.identifier}-${i}`} sx={{ py: 1.5, borderBottom: DIVIDER }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>{h.name}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {formatINR(h.current_value)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {[titleCase(h.provider_name), h.account_ref && `${ACCOUNT_LABEL[category] || 'A/c'}${h.account_count > 1 ? 's' : ''} ${h.account_ref}`].filter(Boolean).join(' · ')}
                      {h.units ? ` · ${formatUnits(h.units)} units` : ''}
                    </Typography>
                    {gain != null && (
                      <Typography
                        variant="caption"
                        sx={{ whiteSpace: 'nowrap', color: gain >= 0 ? 'success.main' : 'error.main' }}
                      >
                        {gain >= 0 ? '+' : '−'}{formatINR(Math.abs(gain))}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
}

export default HoldingsList;
